/**
 * API client
 *
 * ครอบ fetch ไว้ชั้นเดียวเพื่อให้จุดที่ต้องจัดการเหมือนกันทั้งแอปอยู่ที่เดียว:
 *   - แนบ access token · แนบ credentials (refresh token อยู่ใน httpOnly cookie)
 *   - แปลง error เป็น `ApiError` ที่มี `code` อ่านได้ (03-API-SPEC §1.3)
 *   - **ต่ออายุ token อัตโนมัติเมื่อเจอ TOKEN_EXPIRED แล้วลองใหม่ครั้งเดียว**
 *
 * ⚠️ ต้องมี mutex กันเรียก `/auth/refresh` พร้อมกันหลาย request (03 §1.6.2)
 *   ไม่งั้นคำขอที่ยิงพร้อมกันสิบตัวจะหมุน refresh token สิบครั้ง
 *   ตัวที่สองเป็นต้นไปจะเจอ token ที่ถูกเพิกถอนแล้ว → ระบบมองว่าถูกขโมย
 *   → เพิกถอนทั้ง family → **เด็กหลุดออกจากระบบกลางคันทั้งที่ไม่มีอะไรผิด**
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown> | null,
    readonly retryAfter?: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** F1 — งบ AI หมดต้องไม่ทำให้ฟีเจอร์ที่ไม่ใช่ AI ใช้ไม่ได้ตามไปด้วย */
  get isAiBudgetExceeded(): boolean {
    return this.status === 429 && this.code === 'AI_BUDGET_EXCEEDED'
  }
}

let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/** mutex ของ refresh — คำขอที่ชนกันต้องรอตัวเดียวกัน ไม่ใช่ยิงคนละครั้ง */
let refreshing: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!response.ok) return false
      const body = (await response.json()) as { access_token: string }
      accessToken = body.access_token
      return true
    } catch {
      return false
    } finally {
      // ปล่อย mutex ใน microtask ถัดไป เพื่อให้ทุกตัวที่รออยู่ได้ผลของรอบนี้ก่อน
      queueMicrotask(() => {
        refreshing = null
      })
    }
  })()
  return refreshing
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: Record<string, unknown> | null }
  request_id?: string
}

async function toError(response: Response): Promise<ApiError> {
  let body: ErrorEnvelope = {}
  try {
    body = (await response.json()) as ErrorEnvelope
  } catch {
    // บาง error (เช่น 502 จาก Caddy ตอน api ล่ม) ไม่ใช่ JSON
  }
  const retryAfter = response.headers.get('Retry-After')
  return new ApiError(
    response.status,
    body.error?.code ?? 'UNKNOWN',
    body.error?.message ?? response.statusText,
    body.error?.details ?? null,
    retryAfter ? Number(retryAfter) : undefined,
  )
}

async function request<T>(path: string, init: RequestInit, retry = true): Promise<T> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' })

  if (response.status === 401 && retry) {
    const error = await toError(response.clone())
    // ต่ออายุเฉพาะกรณีหมดอายุ — UNAUTHENTICATED แปลว่าไม่มีสิทธิ์จริง ๆ ลองใหม่ก็เท่านั้น
    if (error.code === 'TOKEN_EXPIRED' && (await refreshOnce())) {
      return request<T>(path, init, false)
    }
  }

  if (!response.ok) throw await toError(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

function init(method: string, data?: unknown): RequestInit {
  return data === undefined ? { method } : { method, body: JSON.stringify(data) }
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path, init('GET')),
  post: <T>(path: string, data?: unknown): Promise<T> => request<T>(path, init('POST', data)),
  put: <T>(path: string, data?: unknown): Promise<T> => request<T>(path, init('PUT', data)),
  patch: <T>(path: string, data?: unknown): Promise<T> => request<T>(path, init('PATCH', data)),
  del: <T>(path: string): Promise<T> => request<T>(path, init('DELETE')),
  refresh: refreshOnce,
}

// ─── health (อยู่ที่รากไม่ใช่ใต้ /api) ─────────────────────────
export interface HealthResponse {
  status: 'ok'
}

export async function fetchHealth(): Promise<HealthResponse> {
  const response = await fetch('/healthz')
  if (!response.ok) throw new ApiError(response.status, 'UNHEALTHY', response.statusText)
  return (await response.json()) as HealthResponse
}
